import { useState } from "react";
import { Globe, Target, Check, Info, SlidersHorizontal, Lock } from "lucide-react";
import { SELECTED_CLS, UNSELECTED_CLS } from "../../utils/selectionStyles";

// Moedas principais (bandeiras grandes). A Cooud cobra só estas; a Stripe
// oferece também as do catálogo do backend (gateway.currency_options).
export const STRIPE_CURRENCIES = ["BRL", "USD", "EUR", "GBP"];

// Metadados visuais das moedas (bandeira + símbolo) para o seletor do Stripe.
export const CURRENCY_META = {
    BRL: { flag: "br", label: "BRL", symbol: "R$" },
    USD: { flag: "us", label: "USD", symbol: "$" },
    EUR: { flag: "eu", label: "EUR", symbol: "€" },
    GBP: { flag: "gb", label: "GBP", symbol: "£" },
    CHF: { flag: "ch", label: "CHF", symbol: "Fr" },
    ARS: { flag: "ar", label: "ARS", symbol: "$" },
    MXN: { flag: "mx", label: "MXN", symbol: "MX$" },
    COP: { flag: "co", label: "COP", symbol: "COL$" },
    CLP: { flag: "cl", label: "CLP", symbol: "CLP$" },
    PEN: { flag: "pe", label: "PEN", symbol: "S/" },
    UYU: { flag: "uy", label: "UYU", symbol: "$U" },
    CRC: { flag: "cr", label: "CRC", symbol: "₡" },
    BOB: { flag: "bo", label: "BOB", symbol: "Bs" },
    GTQ: { flag: "gt", label: "GTQ", symbol: "Q" },
    PYG: { flag: "py", label: "PYG", symbol: "₲" },
};

export { SELECTED_CLS, UNSELECTED_CLS };

// Seletor Global/Específico reutilizado por todos os gateways (cartão e PIX).
// Ativo = fundo roxo (--brand), sem ring/border grosso.
export function ScopePicker({ scope, setScope, bots, botIds, toggleBot }) {
    const cardBase = "flex flex-col items-center gap-0.5 px-3 py-2 mb-2 rounded-xl border transition-all cursor-pointer";
    const on = SELECTED_CLS;
    const off = UNSELECTED_CLS;
    return (
        <div className="space-y-4">
            <div className="flex items-center gap-3 pt-2">
                <div className="flex-1 h-px bg-white/[0.04]"></div>
                <span className="text-xs text-white/40 uppercase tracking-wider flex items-center gap-1.5">
                    <SlidersHorizontal size={13} />Escopo do Gateway
                </span>
                <div className="flex-1 h-px bg-white/[0.04]"></div>
            </div>

            <div className="grid grid-cols-2 gap-2">
                <button type="button" onClick={() => setScope('global')} className={`${cardBase} ${scope === 'global' ? on : off}`}>
                    <Globe size={16} className={`transition-colors ${scope === 'global' ? "text-white" : "text-white/40"}`} />
                    <span className={`text-xs font-semibold transition-colors ${scope === 'global' ? "text-white" : "text-white/50"}`}>Global</span>
                    <span className={`text-[10px] leading-tight text-center ${scope === 'global' ? "text-white/70" : "text-white/40"}`}>Todos os bots</span>
                </button>
                <button type="button" onClick={() => setScope('specific')} className={`${cardBase} ${scope === 'specific' ? on : off}`}>
                    <Target size={16} className={`transition-colors ${scope === 'specific' ? "text-white" : "text-white/40"}`} />
                    <span className={`text-xs font-semibold transition-colors ${scope === 'specific' ? "text-white" : "text-white/50"}`}>Específico</span>
                    <span className={`text-[10px] leading-tight text-center ${scope === 'specific' ? "text-white/70" : "text-white/40"}`}>Bots escolhidos</span>
                </button>
            </div>

            {scope === 'specific' && (
                <div className="space-y-4">
                    <div className="rounded-xl border border-white/[0.06] bg-white/[0.015] overflow-hidden">
                        <div className="flex items-center gap-2 px-3.5 py-2.5 border-b border-white/[0.06]">
                            <span className="text-[11px] font-semibold uppercase tracking-wider text-[var(--brand-400)]/80">Bots</span>
                        </div>
                        <div className="max-h-36 overflow-y-auto p-1.5 space-y-1 scrollbar-thin">
                            {(bots || []).map((b) => {
                                const checked = botIds.includes(b.id);
                                return (
                                    <label key={b.id} onClick={() => toggleBot(b.id)}
                                        className="flex items-center gap-2.5 px-3 py-2 rounded-lg cursor-pointer transition-all duration-150 hover:bg-white/[0.04]">
                                        <button type="button" role="checkbox" aria-checked={checked}
                                            className={`h-4 w-4 shrink-0 rounded-sm border flex items-center justify-center transition-colors cursor-pointer ${checked ? "bg-[var(--brand-500)] border-[var(--brand-500)]" : "border-[#52525b]"}`}>
                                            {checked && <Check size={11} className="text-white" />}
                                        </button>
                                        <span className="text-[13px] flex-1 truncate text-white/60">{b.bot_username || b.bot_name || `bot ${b.id}`}</span>
                                        <span className="text-[10px] px-1.5 py-0.5 rounded-md font-medium shrink-0 bg-emerald-500/10 text-emerald-400">Ativo</span>
                                    </label>
                                );
                            })}
                            {(!bots || bots.length === 0) && (
                                <div className="px-3 py-2 text-[13px] text-white/40">Nenhum bot disponível.</div>
                            )}
                        </div>
                    </div>
                    {botIds.length === 0 && (
                        <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-amber-500/5 border border-amber-500/15">
                            <Info size={15} className="text-amber-400 shrink-0" />
                            <p className="text-xs text-amber-400/70">Selecione pelo menos um bot ou volte para <strong className="text-white/60">Global</strong>.</p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

// Quadrado de moeda (bandeira + código + símbolo): selecionado = roxo + marca;
// não selecionado = fundo neutro. `mark` é o ícone dentro da bolinha.
function CurrencyTile({ code, on, mark, ...buttonProps }) {
    const meta = CURRENCY_META[code] || { flag: null, label: code, symbol: code };
    return (
        <button type="button" {...buttonProps}
            className={`relative flex flex-col items-center gap-1.5 p-3 rounded-xl border transition-all ${buttonProps.disabled ? "cursor-default" : "cursor-pointer"} ${on
                ? SELECTED_CLS
                : UNSELECTED_CLS}`}>
            <span className={`absolute top-1.5 right-1.5 w-4 h-4 rounded-full flex items-center justify-center ${on ? "bg-[#a78bfa] text-[#0b0b10]" : "border border-white/25"}`}>
                {on && mark}
            </span>
            {meta.flag && (
                <img alt="" className="w-8 h-6 object-cover rounded-sm"
                    src={`https://flagcdn.com/w40/${meta.flag}.png`} />
            )}
            <span className={`text-xs font-bold ${on ? "text-white" : "text-white/50"}`}>{meta.label}</span>
            <span className={`text-[10px] ${on ? "text-white/70" : "text-white/40"}`}>{meta.symbol}</span>
        </button>
    );
}

const gridCols = (n) => (n > 4 ? "grid-cols-3" : "grid-cols-4");

// Seletor de "Moedas aceitas" (cartão, várias): BRL é sempre aceito (travado).
export function AcceptedCurrencies({ value, onToggle }) {
    return (
        <div className={`grid ${gridCols(STRIPE_CURRENCIES.length)} gap-2`}>
            {STRIPE_CURRENCIES.map((c) => {
                const on = value.includes(c);
                const locked = c === "BRL";
                return (
                    <CurrencyTile key={c} code={c} on={on} disabled={locked} aria-pressed={on}
                        onClick={() => !locked && onToggle(c)}
                        title={locked ? "BRL é sempre aceito" : on ? "Clique para remover" : "Clique para aceitar"}
                        mark={locked ? <Lock size={9} strokeWidth={3} /> : <Check size={11} strokeWidth={3} />} />
                );
            })}
        </div>
    );
}

// Seletor de UMA moeda (ex.: conta Eagle, que processa só a moeda da conta).
export function CurrencyChoice({ value, options, onChange, labelledBy }) {
    return (
        <div role="radiogroup" aria-labelledby={labelledBy} className={`grid ${gridCols(options.length)} gap-2`}>
            {options.map((c) => (
                <CurrencyTile key={c} code={c} on={value === c} role="radio" aria-checked={value === c}
                    aria-label={c} onClick={() => onChange(c)}
                    mark={<Check size={11} strokeWidth={3} />} />
            ))}
        </div>
    );
}

// Escolha única de texto declarada pelo provider (ex.: visual do checkout da
// Eagle). Mesmo visual dos quadrados de moeda, sem bandeira.
export function OptionChoice({ value, options, labels = [], onChange, labelledBy }) {
    return (
        <div role="radiogroup" aria-labelledby={labelledBy} className="grid grid-cols-2 gap-2">
            {options.map((opt, i) => {
                const on = value === opt;
                return (
                    <button key={opt} type="button" role="radio" aria-checked={on} onClick={() => onChange(opt)}
                        className={`relative flex items-center justify-center p-3 pr-7 rounded-xl border transition-all cursor-pointer ${on
                            ? SELECTED_CLS
                            : UNSELECTED_CLS}`}>
                        <span className={`absolute top-1.5 right-1.5 w-4 h-4 rounded-full flex items-center justify-center ${on ? "bg-[#a78bfa] text-[#0b0b10]" : "border border-white/25"}`}>
                            {on && <Check size={11} strokeWidth={3} />}
                        </span>
                        <span className={`text-xs font-bold text-center ${on ? "text-white" : "text-white/50"}`}>{labels[i] || opt}</span>
                    </button>
                );
            })}
        </div>
    );
}

// Seletor de VÁRIAS moedas declaradas pelo provider (ex.: XPag "Moedas aceitas").
export function CurrencyToggles({ value, options, onToggle, labelledBy }) {
    return (
        <div role="group" aria-labelledby={labelledBy} className={`grid ${gridCols(options.length)} gap-2`}>
            {options.map((c) => {
                const on = value.includes(c);
                return (
                    <CurrencyTile key={c} code={c} on={on} aria-pressed={on} aria-label={c}
                        title={on ? "Clique para remover" : "Clique para aceitar"}
                        onClick={() => onToggle(c)}
                        mark={<Check size={11} strokeWidth={3} />} />
                );
            })}
        </div>
    );
}

// Nome da moeda em português (Intl), com fallback no código.
let currencyNames = null;
try { currencyNames = new Intl.DisplayNames(["pt-BR"], { type: "currency" }); } catch { currencyNames = null; }
export const currencyName = (code) => {
    try { return currencyNames?.of(code) || code; } catch { return code; }
};
const fold = (s) => (s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
// Bandeira pelo país da moeda (MXN → mx). Moedas de vários países não têm.
const NO_FLAG = new Set(["XAF", "XCD", "XOF", "XPF", "ANG"]);
const flagOf = (code) => (NO_FLAG.has(code) ? null : (CURRENCY_META[code]?.flag || code.slice(0, 2).toLowerCase()));

// "Mais moedas" (Stripe): busca + lista marcável + etiquetas das escolhidas.
export function MoreCurrencies({ options, value, onToggle, max }) {
    const [query, setQuery] = useState("");
    const selected = options.filter((c) => value.includes(c));
    const atLimit = max != null && value.length >= max;
    const q = fold(query.trim());
    const shown = q
        ? options.filter((c) => fold(c).includes(q) || fold(currencyName(c)).includes(q))
        : options;

    return (
        <div className="space-y-2">
            {selected.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                    {selected.map((c) => (
                        <span key={c} className={`inline-flex items-center gap-1.5 rounded-full border pl-2.5 pr-1 py-0.5 text-[11px] font-semibold text-white/85 ${SELECTED_CLS}`}>
                            {c}
                            <button type="button" aria-label={`Remover ${c}`} onClick={() => onToggle(c)}
                                className="w-4 h-4 rounded-full flex items-center justify-center text-white/50 hover:text-white hover:bg-white/10">×</button>
                        </span>
                    ))}
                </div>
            )}
            <input className="input2" type="search" placeholder="Buscar moeda (nome ou código)"
                value={query} onChange={(e) => setQuery(e.target.value)} />
            {atLimit && (
                <p className="text-[11px] text-amber-300/80">Você chegou ao limite de {max} moedas.</p>
            )}
            <div className="max-h-64 overflow-y-auto space-y-1.5 pr-1">
                {shown.map((c) => {
                    const on = value.includes(c);
                    const blocked = !on && atLimit;
                    const flag = flagOf(c);
                    return (
                        <label key={c}
                            className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border transition-all focus-within:ring-2 focus-within:ring-[#a78bfa]/50 ${on ? SELECTED_CLS : UNSELECTED_CLS} ${blocked ? "opacity-40 cursor-not-allowed" : "cursor-pointer"}`}>
                            <input type="checkbox" className="sr-only" checked={on} disabled={blocked}
                                aria-label={`${c} ${currencyName(c)}`} onChange={() => onToggle(c)} />
                            {flag ? (
                                <img alt="" className="w-7 h-5 shrink-0 object-cover rounded-[4px] ring-1 ring-white/10"
                                    src={`https://flagcdn.com/w40/${flag}.png`} />
                            ) : (
                                <span className="w-7 h-5 shrink-0 rounded-[4px] bg-white/[0.06] ring-1 ring-white/10 flex items-center justify-center">
                                    <Globe size={11} className="text-white/40" />
                                </span>
                            )}
                            <span className="min-w-0 flex-1 flex flex-col leading-tight">
                                <span className={`text-[13px] font-bold tracking-wide ${on ? "text-white" : "text-white/75"}`}>{c}</span>
                                <span className={`text-[11px] truncate ${on ? "text-white/70" : "text-white/40"}`}>{currencyName(c)}</span>
                            </span>
                            <span aria-hidden="true"
                                className={`w-[18px] h-[18px] shrink-0 rounded-full flex items-center justify-center transition-colors ${on ? "bg-[#a78bfa] text-[#0b0b10]" : "border border-white/25"}`}>
                                {on && <Check size={12} strokeWidth={3} />}
                            </span>
                        </label>
                    );
                })}
                {shown.length === 0 && <p className="px-3 py-2 text-xs text-white/40">Nenhuma moeda encontrada.</p>}
            </div>
        </div>
    );
}

// Marcas ainda em fase de testes: selo "BETA" ao lado do nome.
const BETA_GATEWAYS = new Set(["xpag", "pagou", "cakto", "nxgate"]);

export function BetaBadge({ name }) {
    if (!BETA_GATEWAYS.has((name || "").toLowerCase())) return null;
    return (
        <span title="Integração em fase de testes"
            className="inline-flex items-center px-1.5 py-0.5 rounded-md border border-amber-400/30 bg-amber-400/10 text-[10px] font-bold tracking-wider text-amber-300 leading-none">
            BETA
        </span>
    );
}
